"use client";

import { useState } from "react";
import { Img } from "@/components/image";
import { buttonVariants } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { DietHubSectionHeader } from "@/features/hubs/components/diet-hub-section-header";
import type {
  DietHubContentOfferType,
  ProductContentType,
} from "@/features/hubs/types/diet-hub.type";
import { Link } from "@/i18n/navigation";
import { useCarouselDrag } from "@/lib/hooks/use-carousel-drag";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

const DietHubOfferProductCard = ({
  product,
}: {
  product: ProductContentType;
}) => {
  return (
    <div className="group flex h-full flex-col">
      {product.image ? (
        <Link
          className={cn(
            buttonVariants({ variant: "link" }),
            "min-h-0 p-0 text-current",
          )}
          href={product.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Img
            alt={product.image?.alternativeText || product.title}
            src={strapiUrl(product.image?.url)}
            width={product.image?.width}
            height={product.image?.height}
            className="h-[300px] w-full rounded-tl-xl rounded-tr-xl object-cover transition-all duration-200 ease-in-out group-hover:blur-xs group-hover:brightness-95"
          />
        </Link>
      ) : null}
      <div className="rounded-br-xl rounded-bl-xl border-transparent bg-brand-grey1 p-6 transition-all duration-100 ease-in-out group-hover:translate-y-[-4px] group-hover:shadow-[0_12px_28px_-12px_hsl(var(--foodango-green-1)/0.2)]">
        {product.label ? (
          <span className="font-medium text-brand-violet text-sm leading-[22px]">
            {product.label}
          </span>
        ) : null}
        <h3 className="font-medium text-xl leading-7">{product.title}</h3>
        <p className="py-3 text-base leading-6">{product.description}</p>
        <Link
          className={cn(
            buttonVariants({ variant: "link" }),
            "min-h-0 p-0 text-current",
          )}
          href={product.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          {product.linkLabel}
        </Link>
      </div>
    </div>
  );
};

export const DietHubOffer = ({
  content,
}: {
  content: DietHubContentOfferType;
}) => {
  const products = content.product ?? [];

  const [api, setApi] = useState<CarouselApi>();

  const isDragging = useCarouselDrag(api);

  return (
    <section className="py-12">
      <div className="container">
        <div className="space-y-6 lg:space-y-16">
          <DietHubSectionHeader
            label={null}
            title={content.title}
            callback={content.callback}
            description={content.description}
          />
          <div
            className={
              'relative after:absolute after:top-0 after:right-0 after:h-full after:w-14 after:bg-linear-to-r after:from-transparent after:to-white after:content-[""] md:hidden'
            }
          >
            <Carousel
              opts={{
                align: "start",
              }}
              setApi={setApi}
            >
              <CarouselContent className="last:mr-10">
                {products.map((product) => (
                  <CarouselItem className="basis-[88%]" key={product.href}>
                    <DietHubOfferProductCard product={product} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {!isDragging && (
                <>
                  <CarouselPrevious className="left-0 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:-left-4" />
                  <CarouselNext className="right-4 z-10 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:right-10" />
                </>
              )}
            </Carousel>
          </div>
          <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
            {products.map((product) => (
              <DietHubOfferProductCard key={product.href} product={product} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
